import React, { useState } from 'react';
import './PaymentModal.css';

type PaymentMethod = 'creditCard' | 'pix' | 'bankSlip';

interface PaymentData {
  pix?: {
    encodedImage: string;
    payload: string;
  };
  bankSlip?: {
    identificationField: string;
    bankSlipUrl: string;
  };
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentData: PaymentData | null;
  planName: string;
  planPrice: string;
  planPeriod: string;
}

const CreditCardForm = () => (
  <div className="credit-card-form">
    <div className="input-group">
      <label className="input-label">Número do Cartão</label>
      <input type="text" className="input-field" placeholder="0000 0000 0000 0000" />
    </div>
    <div className="input-group">
      <label className="input-label">Nome do Titular</label>
      <input type="text" className="input-field" placeholder="Nome como no cartão" />
    </div>
    <div style={{ display: 'flex', gap: '1rem' }}>
      <div className="input-group" style={{ flex: 1 }}>
        <label className="input-label">Validade</label>
        <input type="text" className="input-field" placeholder="MM/AA" />
      </div>
      <div className="input-group" style={{ flex: 1 }}>
        <label className="input-label">CVV</label>
        <input type="text" className="input-field" placeholder="123" />
      </div>
    </div>
    <button className="btn btn-primary" style={{ width: '100%', marginTop: '1rem' }}>
      Pagar com Cartão
    </button>
  </div>
);

const PixDisplay: React.FC<{ data: PaymentData['pix'] }> = ({ data }) => {
  const [copied, setCopied] = useState(false);
  if (!data) return null;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(data.payload);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="pix-content">
      <p>Escaneie o QR Code ou copie o código abaixo:</p>
      <img src={`data:image/png;base64,${data.encodedImage}`} alt="PIX QR Code" className="pix-qr-code" />
      <div className="pix-payload-container">
        {data.payload}
        <button onClick={copyToClipboard} className="copy-btn">
          {copied ? 'Copiado!' : 'Copiar'}
        </button>
      </div>
    </div>
  );
};

const BankSlipDisplay: React.FC<{ data: PaymentData['bankSlip'] }> = ({ data }) => {
  const [copied, setCopied] = useState(false);
  if (!data) return null;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(data.identificationField);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="bank-slip-content">
      <p>Use a linha digitável ou abra o boleto:</p>
      <div className="identification-field-container">
        <div className="identification-field">{data.identificationField}</div>
        <button onClick={copyToClipboard} className="copy-btn">
          {copied ? 'Copiado!' : 'Copiar'}
        </button>
      </div>
      <div className="bank-slip-actions">
        <a href={data.bankSlipUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
          Abrir Boleto
        </a>
      </div>
    </div>
  );
};


const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  paymentData,
  planName,
  planPrice,
  planPeriod,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('creditCard');

  if (!isOpen) return null;

  return (
    <div className={`modal-overlay ${isOpen ? 'show' : ''}`} onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Finalizar Assinatura</h2>
            <p style={{ margin: 0, color: 'var(--text-secondary)'}}>
              Plano {planName} - R$ {planPrice} / {planPeriod}
            </p>
          </div>
          <button className="close-modal-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="payment-options-selector">
          <button
            className={`payment-option-btn ${paymentMethod === 'creditCard' ? 'active' : ''}`}
            onClick={() => setPaymentMethod('creditCard')}
          >
            Cartão de Crédito
          </button>
          <button
            className={`payment-option-btn ${paymentMethod === 'pix' ? 'active' : ''}`}
            onClick={() => setPaymentMethod('pix')}
          >
            Pix
          </button>
          <button
            className={`payment-option-btn ${paymentMethod === 'bankSlip' ? 'active' : ''}`}
            onClick={() => setPaymentMethod('bankSlip')}
          >
            Boleto
          </button>
        </div>

        <div className="payment-method-content">
          {!paymentData ? (
            <div className="spinner"></div>
          ) : (
            <>
              {paymentMethod === 'creditCard' && <CreditCardForm />}
              {paymentMethod === 'pix' && <PixDisplay data={paymentData.pix} />}
              {paymentMethod === 'bankSlip' && <BankSlipDisplay data={paymentData.bankSlip} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
